import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { arcLabel } from "components/ARC/ARCName";
import { useLocation, useNavigate } from "react-router-dom";

import { ImageOverlay, MapContainer, Polyline, useMap } from "react-leaflet";

import * as L from "leaflet";
import { LatLngBounds } from "leaflet";

import "leaflet/dist/leaflet.css";
import { latLngToPoint2D, point2DToLatLng } from "lib/utils/geometry";
import { formatLocalDateTime, LOCAL_DATETIME_MEDIUM_SHORT } from "lib/utils/dateTimeDisplay";
import {
    LandmarkMarker,
    RobotMarker,
    SelectedPointMarker,
    PointMarker,
    NavigateToPosePointMarker,
    WaypointMarker,
    MissionActionMarker,
} from "./markers";
import WaypointDirectionPath from "./WaypointDirectionPath";
import { Landmark, Robot } from "arcjs/common/arc_devices";
import { MapMetadata } from "arcjs/common/navigation";
import { Point2D } from "arcjs/common/geometry";
import MapViewFallback from "../MapViewFallback";
import {
    logMissionMapPreviewSizes,
    type MissionPreviewOverlayGridSource,
    type NavYamlGeometry,
} from "lib/mission/missionMapPreview";
import PointActionMenu from "../PointActionMenu";
import MappingToggleButton from "../MappingToggleButton";
import CloseButton from "components/layout/CloseButton";
import FunctionalButton from "components/layout/FunctionalButton";
import SearchBar from "components/layout/SearchBar";
import AnimatedModal from "components/layout/motion/AnimatedModal";
import { ReactComponent as PencilIcon } from "assets/svgs/Pencil.svg";
import { ReactComponent as EnlargePreviewIcon } from "assets/svgs/streaming/AddView.svg";
import { ReactComponent as CenterIcon } from "assets/svgs/navigation/map/center.svg";
import { ReactComponent as ClearMapIcon } from "assets/svgs/navigation/map/clear_map.svg";
import { ReactComponent as NavArrowIcon } from "assets/svgs/nav_arrow.svg";
import {
    deleteEditedBmpStored,
    deleteStcmStoredMap,
    fetchStcmFilesList,
    fetchStcmMetadata,
    getStcmMapListBaseUrl,
    getStorageMapPreviewBmpUrl,
    type MapManagerRestorePayload,
    parseEditedBmpsFromResponse,
    postStcmMetadata,
    STCM_REMARKS_MAX_LENGTH,
    storageMapRowLabel,
    stcmRelToCompanionBmpRel,
    type EditedBmpListItem,
    type StcmMapFileMetadata,
} from "lib/consts/stcmMapList";
import { existingStorageMapStemSet, validateAndNormalizeMapBaseName } from "lib/protocols/mapLoad";

const CUSTOM_CRS = L.extend({}, L.CRS.Simple, {
    transformation: new L.Transformation(-1, 0, 1, 0),
    scale: function (zoom: number) {
        return Math.pow(2, zoom);
    },
});

const MAP_OPTS = {
    /** Quarter-step zoom reduces subpixel drift between ImageOverlay and markers. */
    zoomSnap: 0.25,
    autoPanOnFocus: false,
    attributionControl: false,
    crs: CUSTOM_CRS,
    trackResize: true,
    zoomControl: false, // Disable default zoom control
};

export type MarkerClickedCallback<Type> = (
    obj: Type,
    { multiSelect }: { multiSelect: boolean }
) => void;

export type MapClickedCallback = (point: Point2D) => void;
export type NavigateToPoseDragCallback = (pose: { x: number; y: number; theta: number }) => void;

/** Non-navigation mission item rendered next to its anchor waypoint pin. */
export interface MissionActionMarkerInput {
    id: string | number;
    actionType: string;
    point: Point2D;
    /** Stacking slot at the same anchor; 0 is reserved for the NavigateTo pin. */
    stackIndex: number;
}

const EMPTY_ACTION_MARKERS: MissionActionMarkerInput[] = [];

const MissionActionMarkers = ({ markers = EMPTY_ACTION_MARKERS }: { markers?: MissionActionMarkerInput[] }) => (
    <>
        {markers.map((marker) => (
            <MissionActionMarker
                key={marker.id}
                point={marker.point}
                actionType={marker.actionType}
                stackIndex={marker.stackIndex}
                interactive={false}
                bubblingMouseEvents={false}
            />
        ))}
    </>
);

export interface MapViewProps {
    mapViewData?: string;
    mapId?: string;
    mapMetadata?: MapMetadata;
    robots?: Robot[];
    landmarks?: Landmark[];
    waypoints?: any[];
    /** Nav2 plan from /plan_smoothed (preferred) or /plan (map-frame points). */
    plannedPath?: Point2D[];
    onClick?: MapClickedCallback;
    robotOnClick?: MarkerClickedCallback<Robot>;
    landmarkOnClick?: MarkerClickedCallback<Landmark>;
    waypointOnClick?: MarkerClickedCallback<{ id: string | number; pose: { position: Point2D } }>;
    /** Non-navigation mission items (photo/wait/stair/dock) anchored to their waypoint. */
    missionActionMarkers?: MissionActionMarkerInput[];
    selectedPoint?: Point2D;
    onCreateClick?: () => void;
    onChooseClick?: () => void;
    menuVisible?: boolean;
    menuPosition?: Point2D;
    menuOptions?: Array<{ label: string; onClick: () => void }>;
    onMenuClose?: () => void;
    /** Base URL of STCM list API. `null` disables. Omit to use env (REACT_APP_STCM_MAP_LIST_*). */
    stcmListBaseUrl?: string | null;
    /** Called when the user loads a stored map from Map Manager (always the `.stcm` path). */
    onStcmLoadMap?: (
        relativePath: string,
        options?: { variantBmpRelativePath?: string }
    ) => void | Promise<void>;
    /** Called when the user saves the live map with a new base name (no .stcm) and optional remarks. */
    onStcmSaveMap?: (baseName: string, remarks: string) => void | Promise<void>;
    /** Publish clear-map on the robot (after UI confirmation). */
    onStcmClearMap?: () => void;
    /** Hide Map Manager / Clear map when the map is an inset (e.g. MiniView). */
    hideStcmToolbar?: boolean;
    /** Hide mapping on/off toggle (e.g. storage BMP editor). */
    hideMappingToggle?: boolean;
    /** When set, replaces the mapping toggle with this static label (mission map preview). */
    mappingToggleLabel?: string;
    /** Show a loading spinner instead of "No map available" while map data is expected. */
    mapLoading?: boolean;
    /** Stored filter YAML geometry for mission preview size debug logs. */
    missionPreviewFilterYaml?: NavYamlGeometry;
    missionPreviewOverlayGridSource?: MissionPreviewOverlayGridSource;
    /** Open map editor; optional restore payload re-opens Map Manager when returning to control. */
    onStcmEditStorageMap?: (
        relativePath: string,
        mapManagerRestore?: MapManagerRestorePayload
    ) => void;
    /**
     * When true, the viewport initially fits map metadata bounds (Map Editor page only).
     * Default false: fixed center/zoom like the main control map.
     */
    fitMapToBoundsOnLoad?: boolean;
    /** Optional sketch polylines in map frame (map editor drawing). */
    sketchPolylines?: Point2D[][];
    /** When set, map clicks invoke this with the point in map coordinates (e.g. append vertex). */
    onSketchMapClick?: (point: Point2D) => void;
    /** Prevent panning while sketching so clicks do not drag the map. */
    sketchDisableMapDrag?: boolean;
    /** Overrides default sketch polyline Leaflet pathOptions (e.g. color for black ink). */
    sketchPolylineStyle?: { color?: string; weight?: number; opacity?: number };
    /** When set, polylines from sketchPolylines become clickable; index matches that array order. */
    onSketchPolylineClick?: (index: number) => void;
    /** Enables click + drag on map to send NavigateTo pose with heading. */
    onNavigateToPoseDrag?: NavigateToPoseDragCallback;
}

const MAP_INITIAL_FIT_MAX_ZOOM = 10;
const MAP_INITIAL_FIT_PADDING: [number, number] = [24, 24];
/** Between base map ImageOverlay and marker pane (600). */
const PLANNED_PATH_PANE = "planned-path-pane";
const PLANNED_PATH_PANE_Z_INDEX = 500;
/** Map-frame drift (meters) before follow treats the robot as off-center. */
const ROBOT_FOLLOW_DEAD_ZONE_METERS = 0.1;
/** Pose change below this (meters) between updates counts as stationary. */
const ROBOT_FOLLOW_STATIONARY_MOVE_METERS = 0.02;
/** Apply jitter dead zone after the robot has been stationary this long (ms). */
const ROBOT_FOLLOW_STATIONARY_MS = 1500;

type FollowViewState = {
    lastTarget: L.LatLngLiteral | null;
    lastSignificantMoveAt: number;
};

const createFollowViewState = (): FollowViewState => ({
    lastTarget: null,
    lastSignificantMoveAt: Date.now(),
});

const followDriftMeters = (a: L.LatLngLiteral, b: L.LatLngLiteral) => {
    const dx = b.lng - a.lng;
    const dy = b.lat - a.lat;
    return Math.hypot(dx, dy);
};

const shouldRecenterFollowView = (
    map: L.Map,
    target: L.LatLngLiteral,
    state: FollowViewState,
    { force = false } = {}
): { recenter: boolean; nextState: FollowViewState } => {
    if (force) {
        return { recenter: true, nextState: createFollowViewState() };
    }

    const now = Date.now();
    const center = map.getCenter();
    const centerLiteral = { lat: center.lat, lng: center.lng };
    const driftFromCenter = followDriftMeters(centerLiteral, target);
    const movedSinceLast =
        state.lastTarget == null
            ? Number.POSITIVE_INFINITY
            : followDriftMeters(state.lastTarget, target);

    const lastSignificantMoveAt =
        movedSinceLast >= ROBOT_FOLLOW_STATIONARY_MOVE_METERS ? now : state.lastSignificantMoveAt;

    const nextState: FollowViewState = {
        lastTarget: target,
        lastSignificantMoveAt,
    };

    if (driftFromCenter > ROBOT_FOLLOW_DEAD_ZONE_METERS) {
        return { recenter: true, nextState };
    }

    if (movedSinceLast >= ROBOT_FOLLOW_STATIONARY_MOVE_METERS) {
        return { recenter: true, nextState };
    }

    const stationaryLongEnough = now - lastSignificantMoveAt >= ROBOT_FOLLOW_STATIONARY_MS;

    if (stationaryLongEnough && driftFromCenter <= ROBOT_FOLLOW_DEAD_ZONE_METERS) {
        return { recenter: false, nextState };
    }

    return { recenter: true, nextState };
};

const recenterFollowView = (
    map: L.Map,
    target: L.LatLngLiteral,
    state: FollowViewState,
    options: { force?: boolean } = {}
): FollowViewState => {
    const { recenter, nextState } = shouldRecenterFollowView(map, target, state, options);
    if (recenter) {
        map.setView(target, map.getZoom(), { animate: false });
    }
    return nextState;
};

/** Fit the map bounds in the viewport on load and when bounds change. */
const MapInitialFitBounds = ({ bounds }: { bounds: LatLngBounds | null }) => {
    const map = useMap();
    useEffect(() => {
        if (!bounds || !bounds.isValid()) return;

        const fit = () => {
            map.invalidateSize();
            map.fitBounds(bounds, {
                padding: MAP_INITIAL_FIT_PADDING,
                maxZoom: MAP_INITIAL_FIT_MAX_ZOOM,
            });
        };

        fit();
        const raf = requestAnimationFrame(fit);
        const t = window.setTimeout(fit, 50);
        return () => {
            cancelAnimationFrame(raf);
            window.clearTimeout(t);
        };
    }, [map, bounds]);

    return null;
};

/**
 * MapContainer only applies `dragging` / zoom handlers at map construction time.
 * Mount this while sketching so pan/zoom are disabled; unmount restores handlers.
 */
const SketchMapInteractionLock = () => {
    const map = useMap();
    useEffect(() => {
        const drag = map.dragging;
        const wheel = map.scrollWheelZoom;
        const touch = map.touchZoom;
        drag?.disable();
        wheel?.disable();
        touch?.disable();
        return () => {
            drag?.enable();
            wheel?.enable();
            touch?.enable();
        };
    }, [map]);
    return null;
};

/** Keeps viewport centered on robot while follow mode is enabled. */
const RobotFollowController = ({
    enabled,
    target,
}: {
    enabled: boolean;
    target: L.LatLngLiteral | null;
}) => {
    const map = useMap();
    const draggingRef = useRef(false);
    /** Wheel/pinch zoom — skip setView until zoomend to avoid overlay/marker desync. */
    const zoomingRef = useRef(false);
    const followStateRef = useRef(createFollowViewState());
    const enabledRef = useRef(enabled);
    const targetRef = useRef(target);
    enabledRef.current = enabled;
    targetRef.current = target;

    const applyFollow = useCallback(
        (force = false) => {
            const t = targetRef.current;
            if (!enabledRef.current || !t || draggingRef.current) return;
            if (zoomingRef.current && !force) return;
            followStateRef.current = recenterFollowView(map, t, followStateRef.current, {
                force,
            });
        },
        [map]
    );

    useEffect(() => {
        if (!enabled) {
            followStateRef.current = createFollowViewState();
            draggingRef.current = false;
            zoomingRef.current = false;
            return;
        }
        applyFollow();
    }, [enabled, target, applyFollow]);

    useEffect(() => {
        if (!enabled) return;

        const onDragStart = () => {
            draggingRef.current = true;
        };

        const onDragEnd = () => {
            draggingRef.current = false;
            if (!targetRef.current) return;
            applyFollow(true);
        };

        const onZoomStart = () => {
            zoomingRef.current = true;
        };

        const onZoomEnd = () => {
            zoomingRef.current = false;
            requestAnimationFrame(() => {
                map.invalidateSize({ animate: false });
                applyFollow(true);
            });
        };

        map.on("dragstart", onDragStart);
        map.on("dragend", onDragEnd);
        map.on("zoomstart", onZoomStart);
        map.on("zoomend", onZoomEnd);

        return () => {
            map.off("dragstart", onDragStart);
            map.off("dragend", onDragEnd);
            map.off("zoomstart", onZoomStart);
            map.off("zoomend", onZoomEnd);
        };
    }, [map, enabled, applyFollow]);

    useEffect(() => {
        if (!enabled) return;

        const container = map.getContainer();
        if (!container) return;

        const sync = () => {
            requestAnimationFrame(() => {
                map.invalidateSize({ animate: false });
                applyFollow();
            });
        };

        let ro: ResizeObserver | null = null;
        if (typeof ResizeObserver !== "undefined") {
            ro = new ResizeObserver(() => sync());
            ro.observe(container);
        }

        window.addEventListener("resize", sync);

        return () => {
            ro?.disconnect();
            window.removeEventListener("resize", sync);
        };
    }, [map, enabled, applyFollow]);

    return null;
};

/** Leaflet pane so Nav2 plan renders above the base map image. */
const PlannedPathPane = () => {
    const map = useMap();

    useEffect(() => {
        if (!map.getPane(PLANNED_PATH_PANE)) {
            map.createPane(PLANNED_PATH_PANE);
        }
        const pane = map.getPane(PLANNED_PATH_PANE);
        if (pane) {
            pane.style.zIndex = String(PLANNED_PATH_PANE_Z_INDEX);
        }
    }, [map]);

    return null;
};

/**
 * Leaflet's `trackResize` only reacts to the window, not to element size changes (e.g. MiniView
 * drag-resize). Sync pixel size when the container changes.
 */
const MapContainerResizeSync = () => {
    const map = useMap();

    useEffect(() => {
        const container = map.getContainer();
        if (!container) return;

        const sync = () => {
            requestAnimationFrame(() => {
                map.invalidateSize({ animate: false });
            });
        };

        let ro: ResizeObserver | null = null;
        if (typeof ResizeObserver !== "undefined") {
            ro = new ResizeObserver(() => sync());
            ro.observe(container);
        }

        window.addEventListener("resize", sync);
        sync();

        return () => {
            ro?.disconnect();
            window.removeEventListener("resize", sync);
        };
    }, [map]);

    return null;
};

/** Re-sync layer projection after wheel/pinch zoom (fractional zoomSnap). */
const MapZoomInvalidateSync = () => {
    const map = useMap();

    useEffect(() => {
        const onZoomEnd = () => {
            requestAnimationFrame(() => {
                map.invalidateSize({ animate: false });
            });
        };

        map.on("zoomend", onZoomEnd);
        return () => {
            map.off("zoomend", onZoomEnd);
        };
    }, [map]);

    return null;
};

/** Handles press-hold-drag-release for NavigateTo pose selection. */
const NavigateToPoseDragController = ({
    enabled,
    onDragStart,
    onDragMove,
    onDragEnd,
}: {
    enabled: boolean;
    onDragStart: (point: Point2D) => void;
    onDragMove: (point: Point2D) => void;
    onDragEnd: (point: Point2D) => void;
}) => {
    const map = useMap();
    const draggingRef = useRef(false);
    const lastPointRef = useRef<Point2D | null>(null);

    useEffect(() => {
        if (!enabled) {
            draggingRef.current = false;
            return;
        }

        const container = map.getContainer();
        const overlayImg = container.querySelector(
            ".leaflet-image-layer"
        ) as HTMLImageElement | null;
        const onNativeDragStart = (e: Event) => e.preventDefault();
        if (overlayImg) {
            overlayImg.draggable = false;
            overlayImg.style.userSelect = "none";
            (overlayImg.style as CSSStyleDeclaration & { webkitUserDrag?: string }).webkitUserDrag = "none";
            overlayImg.addEventListener("dragstart", onNativeDragStart);
        }

        const handlePointerDown = (e: PointerEvent) => {
            const target = e.target as HTMLElement | null;
            const clickedUiControl = Boolean(
                target?.closest(
                    ".leaflet-control, button, [role='button'], [data-nav-pose-control='true']"
                )
            );
            if (clickedUiControl || e.button !== 0) return;
            e.preventDefault();
            draggingRef.current = true;
            try {
                container.setPointerCapture(e.pointerId);
            } catch {
                // Ignore if capture is unavailable.
            }
            const containerPoint = map.mouseEventToContainerPoint(e as unknown as MouseEvent);
            const latLng = map.containerPointToLatLng(containerPoint);
            const pt = latLngToPoint2D(latLng);
            lastPointRef.current = pt;
            onDragStart(pt);
        };

        const handleWindowPointerMove = (e: PointerEvent) => {
            if (!draggingRef.current) return;
            const containerPoint = map.mouseEventToContainerPoint(e as unknown as MouseEvent);
            const latLng = map.containerPointToLatLng(containerPoint);
            const pt = latLngToPoint2D(latLng);
            lastPointRef.current = pt;
            onDragMove(pt);
        };

        const handleWindowPointerUp = (e: PointerEvent) => {
            if (!draggingRef.current) return;
            draggingRef.current = false;
            try {
                container.releasePointerCapture(e.pointerId);
            } catch {
                // Ignore if capture is unavailable.
            }
            const containerPoint = map.mouseEventToContainerPoint(e as unknown as MouseEvent);
            const latLng = map.containerPointToLatLng(containerPoint);
            const pt = latLngToPoint2D(latLng);
            lastPointRef.current = pt;
            onDragEnd(pt);
        };

        const handleWindowPointerCancel = (e: PointerEvent) => {
            if (!draggingRef.current) return;
            draggingRef.current = false;
            try {
                container.releasePointerCapture(e.pointerId);
            } catch {
                // Ignore if capture is unavailable.
            }
            onDragEnd(lastPointRef.current ?? latLngToPoint2D(map.getCenter()));
        };

        container.style.touchAction = "none";
        container.addEventListener("pointerdown", handlePointerDown);
        window.addEventListener("pointermove", handleWindowPointerMove);
        window.addEventListener("pointerup", handleWindowPointerUp);
        window.addEventListener("pointercancel", handleWindowPointerCancel);

        return () => {
            container.style.touchAction = "";
            container.removeEventListener("pointerdown", handlePointerDown);
            window.removeEventListener("pointermove", handleWindowPointerMove);
            window.removeEventListener("pointerup", handleWindowPointerUp);
            window.removeEventListener("pointercancel", handleWindowPointerCancel);
            if (overlayImg) {
                overlayImg.removeEventListener("dragstart", onNativeDragStart);
            }
            draggingRef.current = false;
            lastPointRef.current = null;
        };
    }, [enabled, map, onDragStart, onDragMove, onDragEnd]);

    return null;
};

const mapMetadataToBounds = (metadata: MapMetadata | undefined): LatLngBounds | null => {
    if (!metadata) return null;
    // In ROS OccupancyGrid, origin is the position of the LOWER-LEFT corner of the map (in meters)
    // width/height are in cells, so multiply by resolution (meters/cell) to get actual size
    const o = metadata.origin;
    const widthMeters = metadata.width * metadata.resolution;
    const heightMeters = metadata.height * metadata.resolution;
    const southWestCorner = point2DToLatLng({ x: o.x, y: o.y });
    const northEastCorner = point2DToLatLng({ x: o.x + widthMeters, y: o.y + heightMeters });
    return new LatLngBounds(southWestCorner, northEastCorner);
};

const LandmarkMarkers = ({
    landmarks,
    landmarkOnClick,
}: {
    landmarks: Landmark[];
    landmarkOnClick: MarkerClickedCallback<Landmark>;
}) => {
    return (
        <>
            {landmarks.reduce((acc, landmark) => {
                const { pose, name } = landmark;
                if (!pose) return acc;

                return [
                    ...acc,
                    <LandmarkMarker
                        title={name}
                        key={name}
                        pose={pose}
                        eventHandlers={{
                            click: landmarkOnClick
                                ? (e) => {
                                      const multiSelect = e.originalEvent.shiftKey;
                                      landmarkOnClick(landmark, { multiSelect });
                                  }
                                : undefined,
                        }}
                        bubblingMouseEvents={false}
                    />,
                ];
            }, [] as React.ReactNode[])}
        </>
    );
};

const RobotMarkers = ({
    robots,
    robotOnClick,
}: {
    robots: Robot[];
    robotOnClick?: MarkerClickedCallback<Robot> | null;
}) => {
    const clickShouldBubble = !robotOnClick;
    const interactive = !!robotOnClick;
    return (
        <>
            {robots.reduce((acc, robot) => {
                const { pose, serialNumber, name } = robot;
                if (!pose) return acc;

                const handlers: L.LeafletEventHandlerFnMap = {};
                if (robotOnClick)
                    handlers.click = (e) => {
                        const multiSelect = e.originalEvent.shiftKey;
                        robotOnClick(robot, { multiSelect });
                    };
                return [
                    ...acc,

                    <RobotMarker
                        title={arcLabel({ serialNumber, name })}
                        key={serialNumber}
                        pose={pose}
                        eventHandlers={handlers}
                        bubblingMouseEvents={clickShouldBubble}
                        interactive={interactive}
                    ></RobotMarker>,
                ];
            }, [] as React.ReactNode[])}
        </>
    );
};

const WaypointMarkers = ({
    waypoints,
    compact = false,
    waypointOnClick,
}: {
    waypoints: any[];
    compact?: boolean;
    waypointOnClick?: MarkerClickedCallback<{ id: string | number; pose: { position: Point2D } }>;
}) => {
    const clickHandler = waypointOnClick
        ? (waypoint: { id: string | number; pose: { position: Point2D } }) =>
              (e: L.LeafletMouseEvent) => {
                  const multiSelect = e.originalEvent.shiftKey;
                  waypointOnClick(waypoint, { multiSelect });
              }
        : undefined;

    return (
        <>
            {waypoints.map((waypoint, index) => {
                const point = { x: waypoint.pose.position.x, y: waypoint.pose.position.y };
                const eventHandlers = clickHandler ? { click: clickHandler(waypoint) } : undefined;
                // Same indexed pin in both inset and full map views.
                return (
                    <WaypointMarker
                        key={waypoint.id}
                        point={point}
                        index={index}
                        size={compact ? "compact" : "default"}
                        eventHandlers={eventHandlers}
                        bubblingMouseEvents={false}
                    />
                );
            })}
        </>
    );
};

const plannedPathOptions = {
    color: "#3b82f6",
    weight: 3,
    opacity: 0.9,
};

const sketchPathOptions = {
    color: "#dc2626",
    weight: 4,
    opacity: 1,
};

// Stable default so that when parent doesn't pass waypoints, we don't get a new [] every render (avoids useEffect/useMemo churn)
const EMPTY_WAYPOINTS: any[] = [];
const EMPTY_PLANNED_PATH: Point2D[] = [];

const formatSavedAtLabel = (iso: string): string =>
    formatLocalDateTime(iso, LOCAL_DATETIME_MEDIUM_SHORT);

const dedupeNormalized = (paths: string[]): string[] => {
    const seen = new Set<string>();
    const out: string[] = [];
    for (const p of paths) {
        const normalized = p.replace(/\\/g, "/").trim();
        if (!normalized || seen.has(normalized)) continue;
        seen.add(normalized);
        out.push(normalized);
    }
    return out;
};

const StcmClearMapDialog = ({
    visible,
    onClose,
    onClear,
    onChooseSave,
    /** Higher than Map Manager (10000) when opened from Map Manager footer. */
    zClass = "z-[10000]",
}: {
    visible: boolean;
    onClose: () => void;
    onClear?: () => void;
    onChooseSave?: () => void;
    zClass?: string;
}) => {
    const handleSaveInstead = useCallback(() => {
        onClose();
        onChooseSave?.();
    }, [onClose, onChooseSave]);

    const handleConfirmClear = useCallback(() => {
        onClose();
        onClear?.();
    }, [onClose, onClear]);

    return (
        <AnimatedModal
            visible={visible}
            onClose={onClose}
            className="w-full max-w-sm rounded-r10 border border-black80 bg-black90 p-3 shadow-lg"
            zIndexClassName={zClass}
        >
            <div className="mb-2 flex items-center justify-between gap-2">
                <h3 className="text-h5 font-semibold">Clear map</h3>
                <CloseButton className="text-black60 hover:text-black40" onClick={onClose} />
            </div>
            <p className="text-h5 mb-3 text-black40">
                Clearing the map on the robot cannot be undone. Save the map first if you need a
                copy, or continue to clear.
            </p>
            <div className="flex flex-wrap justify-end gap-2 border-t border-black80 pt-2">
                <FunctionalButton
                    className="rounded-r5 border border-black80 bg-black70 px-3 py-1.5 hover:bg-black80"
                    title="Cancel"
                    onClick={onClose}
                />
                <FunctionalButton
                    className="rounded-r5 border border-black80 bg-black70 px-3 py-1.5 hover:bg-black80"
                    title="Save map"
                    disabled={!onChooseSave}
                    onClick={handleSaveInstead}
                />
                <FunctionalButton
                    className="text-h5 rounded-r5 border border-red-900/80 bg-black70 px-3 py-1.5 hover:bg-black80"
                    titleClassName="!normal-case text-red-400"
                    title="Clear map"
                    disabled={!onClear}
                    onClick={handleConfirmClear}
                />
            </div>
        </AnimatedModal>
    );
};

/** Compact clear-map confirmation next to Map Manager on the map toolbar. */
export const StcmClearMapControl = ({
    onClear,
    onChooseSave,
}: {
    onClear?: () => void;
    /** Opens Map Manager with the save-name dialog (user saves before clearing). */
    onChooseSave?: () => void;
}) => {
    const [modalOpen, setModalOpen] = useState(false);
    const closeModal = useCallback(() => setModalOpen(false), []);

    return (
        <>
            <FunctionalButton
                className="h-10 w-10 shrink-0 rounded-r5 border border-amber-700 bg-amber-500 text-white hover:bg-amber-400"
                icon={<ClearMapIcon className="h-5 w-5" />}
                onClick={() => setModalOpen(true)}
            />
            <StcmClearMapDialog
                visible={modalOpen}
                onClose={closeModal}
                onClear={onClear}
                onChooseSave={onChooseSave}
            />
        </>
    );
};

/** Load / save STCM maps: one toolbar entry, main dialog + optional name dialog for save. */
const StcmMapManagerControl = ({
    baseUrl,
    onLoadMap,
    onEditStorageMap,
    onSave,
    onStcmClearMap,
    onClearMapChooseSave,
    saveModalRequestSerial = 0,
    mapManagerSaveNameFlowSerial = 0,
    liveMapPreviewUrl,
}: {
    baseUrl: string;
    onLoadMap?: (
        relativePath: string,
        options?: { variantBmpRelativePath?: string }
    ) => void | Promise<void>;
    onEditStorageMap?: (relativePath: string, mapManagerRestore?: MapManagerRestorePayload) => void;
    onSave?: (baseName: string, remarks: string) => void | Promise<void>;
    onStcmClearMap?: () => void;
    onClearMapChooseSave?: () => void;
    /** Increment (from parent) to open Map Manager without using the toolbar button. */
    saveModalRequestSerial?: number;
    /** Increment (from parent) to open Map Manager and the save-name dialog together. */
    mapManagerSaveNameFlowSerial?: number;
    liveMapPreviewUrl?: string | null;
}) => {
    const location = useLocation();
    const navigate = useNavigate();
    const [mainOpen, setMainOpen] = useState(false);
    const [clearMapModalOpen, setClearMapModalOpen] = useState(false);
    const [saveNameOpen, setSaveNameOpen] = useState(false);
    const openRequestSeenRef = useRef(0);
    const saveNameFlowSeenRef = useRef(0);
    const [files, setFiles] = useState<string[]>([]);
    const [editedBmps, setEditedBmps] = useState<EditedBmpListItem[]>([]);
    const [listLoading, setListLoading] = useState(false);
    const [listError, setListError] = useState<string | null>(null);
    const [mapName, setMapName] = useState("");
    const [saveSubmitBusy, setSaveSubmitBusy] = useState(false);
    const [mapRemarks, setMapRemarks] = useState("");
    const [previewSelection, setPreviewSelection] = useState<"live" | string>("live");
    /** Selected `.stcm` row; stays set when previewing an edited image variant. */
    const [focusedStcm, setFocusedStcm] = useState<string | null>(null);
    const [filePreviewFailed, setFilePreviewFailed] = useState(false);
    const [storageMeta, setStorageMeta] = useState<StcmMapFileMetadata | null>(null);
    const [storageMetaLoading, setStorageMetaLoading] = useState(false);
    const [storageSavedAtByFile, setStorageSavedAtByFile] = useState<Record<string, string>>({});
    const [storageDatesLoading, setStorageDatesLoading] = useState(false);
    const [deleteBusy, setDeleteBusy] = useState(false);
    const [deleteError, setDeleteError] = useState<string | null>(null);
    const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
    const [deleteTargetPath, setDeleteTargetPath] = useState<string | null>(null);
    const [deleteConfirmInput, setDeleteConfirmInput] = useState("");
    const [storageRemarkModalOpen, setStorageRemarkModalOpen] = useState(false);
    const [storageRemarkDraft, setStorageRemarkDraft] = useState("");
    const [storageRemarkSaving, setStorageRemarkSaving] = useState(false);
    const [storageRemarkSaveError, setStorageRemarkSaveError] = useState<string | null>(null);
    const [previewEnlargeOpen, setPreviewEnlargeOpen] = useState(false);
    const [mapSearchQuery, setMapSearchQuery] = useState("");
    const lastMapManagerRestoreApplied = useRef<string | null>(null);

    useEffect(() => {
        const st = location.state as {
            mapManagerRestore?: MapManagerRestorePayload;
        } | null;
        const r = st?.mapManagerRestore;
        if (!r?.focusedStcm) {
            return;
        }
        const key = JSON.stringify(r);
        if (lastMapManagerRestoreApplied.current === key) {
            return;
        }
        lastMapManagerRestoreApplied.current = key;
        setMainOpen(true);
        setFocusedStcm(r.focusedStcm);
        setPreviewSelection(
            r.previewSelection && r.previewSelection !== "live" ? r.previewSelection : r.focusedStcm
        );
        const { mapManagerRestore: _drop, ...rest } = (location.state ?? {}) as Record<
            string,
            unknown
        >;
        navigate(
            {
                pathname: location.pathname,
                search: location.search,
                hash: location.hash,
            },
            {
                replace: true,
                state: Object.keys(rest).length > 0 ? rest : undefined,
            }
        );
    }, [location.state, location.pathname, location.search, location.hash, navigate]);

    const displayedStorageRemark = useMemo(() => {
        if (!storageMeta) return "";
        const norm = (p: string) => p.replace(/\\/g, "/").trim();
        if (previewSelection === "live") return storageMeta.remarks ?? "";
        const ps = norm(previewSelection);
        const variant = storageMeta.edited_bmp_variants?.find((v) => norm(v.bmp_rel_path) === ps);
        if (variant) return variant.remarks ?? "";
        return storageMeta.remarks ?? "";
    }, [storageMeta, previewSelection]);

    useEffect(() => {
        setFilePreviewFailed(false);
        setDeleteError(null);
        setStorageRemarkModalOpen(false);
        setStorageRemarkDraft("");
        setStorageRemarkSaveError(null);
        setPreviewEnlargeOpen(false);
    }, [previewSelection]);

    useEffect(() => {
        if (!focusedStcm || !mainOpen) {
            setStorageMeta(null);
            setStorageMetaLoading(false);
            return;
        }
        let cancelled = false;
        setStorageMeta(null);
        setStorageMetaLoading(true);
        fetchStcmMetadata(baseUrl, focusedStcm)
            .then((meta) => {
                if (!cancelled) setStorageMeta(meta);
            })
            .catch(() => {
                if (!cancelled) setStorageMeta(null);
            })
            .finally(() => {
                if (!cancelled) setStorageMetaLoading(false);
            });
        return () => {
            cancelled = true;
        };
    }, [focusedStcm, baseUrl, mainOpen]);

    useEffect(() => {
        if (!mainOpen || listLoading) return;
        const fromEdited: Record<string, string> = {};
        for (const e of editedBmps) {
            if (e.saved_at) {
                fromEdited[e.path] = e.saved_at;
            }
        }
        if (!files.length) {
            setStorageSavedAtByFile(fromEdited);
            setStorageDatesLoading(false);
            return;
        }
        let cancelled = false;
        setStorageDatesLoading(true);
        Promise.all(
            files.map((f) =>
                fetchStcmMetadata(baseUrl, f)
                    .then((m) => [f, m?.saved_at ?? null] as const)
                    .catch(() => [f, null] as const)
            )
        ).then((results) => {
            if (cancelled) return;
            const next: Record<string, string> = { ...fromEdited };
            for (const [f, at] of results) {
                if (at) next[f] = at;
            }
            setStorageSavedAtByFile(next);
            setStorageDatesLoading(false);
        });
        return () => {
            cancelled = true;
        };
    }, [mainOpen, baseUrl, files, listLoading, editedBmps]);

    useEffect(() => {
        if (!mainOpen) return;

        let cancelled = false;
        setListLoading(true);
        setListError(null);
        setPreviewSelection("live");
        setFocusedStcm(null);
        setStorageSavedAtByFile({});
        setStorageDatesLoading(false);
        setEditedBmps([]);

        fetchStcmFilesList(baseUrl)
            .then((data) => {
                if (cancelled) return;
                setFiles(dedupeNormalized(Array.isArray(data.files) ? data.files : []));
                const parsedEdited = parseEditedBmpsFromResponse(data.edited_bmps);
                const seenEdited = new Set<string>();
                setEditedBmps(
                    parsedEdited.filter((x) => {
                        if (seenEdited.has(x.path)) return false;
                        seenEdited.add(x.path);
                        return true;
                    })
                );
            })
            .catch((e) => {
                if (cancelled) return;
                setListError(e instanceof Error ? e.message : "Failed to load");
                setFiles([]);
            })
            .finally(() => {
                if (!cancelled) setListLoading(false);
            });

        return () => {
            cancelled = true;
        };
    }, [mainOpen, baseUrl]);

    useEffect(() => {
        if (saveModalRequestSerial > openRequestSeenRef.current) {
            openRequestSeenRef.current = saveModalRequestSerial;
            setMainOpen(true);
        }
    }, [saveModalRequestSerial]);

    useEffect(() => {
        if (mapManagerSaveNameFlowSerial > saveNameFlowSeenRef.current) {
            saveNameFlowSeenRef.current = mapManagerSaveNameFlowSerial;
            setMapName("");
            setMainOpen(true);
            setSaveNameOpen(true);
        }
    }, [mapManagerSaveNameFlowSerial]);

    const closeDeleteConfirm = useCallback(() => {
        setDeleteConfirmOpen(false);
        setDeleteTargetPath(null);
        setDeleteConfirmInput("");
        setDeleteBusy(false);
        setDeleteError(null);
    }, []);

    const closeMain = useCallback(() => {
        setMainOpen(false);
        setSaveNameOpen(false);
        setClearMapModalOpen(false);
        setListError(null);
        setMapName("");
        setMapRemarks("");
        setPreviewSelection("live");
        setFilePreviewFailed(false);
        setDeleteBusy(false);
        setDeleteError(null);
        setDeleteConfirmOpen(false);
        setDeleteTargetPath(null);
        setDeleteConfirmInput("");
        setStorageRemarkModalOpen(false);
        setStorageRemarkDraft("");
        setStorageRemarkSaving(false);
        setStorageRemarkSaveError(null);
        setPreviewEnlargeOpen(false);
        setMapSearchQuery("");
        lastMapManagerRestoreApplied.current = null;
    }, []);

    const closePreviewEnlarge = useCallback(() => {
        setPreviewEnlargeOpen(false);
    }, []);

    const closeStorageRemarkModal = useCallback(() => {
        if (storageRemarkSaving) return;
        setStorageRemarkModalOpen(false);
        setStorageRemarkDraft("");
        setStorageRemarkSaveError(null);
    }, [storageRemarkSaving]);

    const confirmStorageRemark = useCallback(async () => {
        if (!focusedStcm || storageRemarkSaving) return;
        setStorageRemarkSaving(true);
        setStorageRemarkSaveError(null);
        try {
            const norm = (p: string) => p.replace(/\\/g, "/").trim();
            const ps = norm(previewSelection);
            const isVariantRemark =
                previewSelection !== "live" &&
                Boolean(storageMeta?.edited_bmp_variants?.some((v) => norm(v.bmp_rel_path) === ps));
            await postStcmMetadata(
                baseUrl,
                focusedStcm,
                storageRemarkDraft.trim(),
                isVariantRemark ? { variantBmpRelPath: previewSelection } : undefined
            );
            const updated = await fetchStcmMetadata(baseUrl, focusedStcm);
            setStorageMeta(updated);
            const savedAt = updated?.saved_at;
            if (savedAt) {
                setStorageSavedAtByFile((prev) => ({
                    ...prev,
                    [focusedStcm]: savedAt,
                }));
            }
            setStorageRemarkModalOpen(false);
            setStorageRemarkDraft("");
        } catch (e) {
            setStorageRemarkSaveError(e instanceof Error ? e.message : "Could not save remark");
        } finally {
            setStorageRemarkSaving(false);
        }
    }, [
        focusedStcm,
        baseUrl,
        storageRemarkDraft,
        storageRemarkSaving,
        previewSelection,
        storageMeta,
    ]);

    const openDeleteConfirm = useCallback(() => {
        if (previewSelection === "live") return;
        setDeleteTargetPath(previewSelection);
        setDeleteConfirmInput("");
        setDeleteError(null);
        setDeleteConfirmOpen(true);
    }, [previewSelection]);

    const executeDeleteStoredMap = useCallback(async () => {
        if (!deleteTargetPath || deleteConfirmInput !== "CONFIRM" || deleteBusy) return;
        setDeleteBusy(true);
        setDeleteError(null);
        try {
            if (/\.(bmp|png)$/i.test(deleteTargetPath)) {
                await deleteEditedBmpStored(baseUrl, deleteTargetPath);
                setEditedBmps((prev) => prev.filter((e) => e.path !== deleteTargetPath));
                const stcmFor = focusedStcm;
                if (stcmFor) {
                    try {
                        const updated = await fetchStcmMetadata(baseUrl, stcmFor);
                        setStorageMeta(updated);
                    } catch {
                        /* ignore */
                    }
                    setPreviewSelection(stcmFor);
                } else {
                    setPreviewSelection("live");
                }
            } else {
                const stcmRel = deleteTargetPath;
                const normPath = (p: string) => p.replace(/\\/g, "/").trim();
                let variantPaths: string[] = [];
                try {
                    const meta = await fetchStcmMetadata(baseUrl, stcmRel);
                    variantPaths =
                        meta.edited_bmp_variants
                            ?.map((v) => normPath(v.bmp_rel_path))
                            .filter((p) => p.length > 0) ?? [];
                } catch {
                    variantPaths =
                        storageMeta?.edited_bmp_variants
                            ?.map((v) => normPath(v.bmp_rel_path))
                            .filter((p) => p.length > 0) ?? [];
                }
                for (const p of variantPaths) {
                    await deleteEditedBmpStored(baseUrl, p);
                }
                await deleteStcmStoredMap(baseUrl, stcmRel);
                setFiles((prev) => prev.filter((f) => f !== stcmRel));
                const variantSet = new Set(variantPaths);
                setEditedBmps((prev) => prev.filter((e) => !variantSet.has(normPath(e.path))));
                setFocusedStcm(null);
                setPreviewSelection("live");
            }
            closeDeleteConfirm();
        } catch (e) {
            setDeleteError(e instanceof Error ? e.message : "Delete failed");
        } finally {
            setDeleteBusy(false);
        }
    }, [
        deleteTargetPath,
        deleteConfirmInput,
        deleteBusy,
        baseUrl,
        closeDeleteConfirm,
        focusedStcm,
        storageMeta,
    ]);

    const openSaveName = useCallback(() => {
        setMapName("");
        setMapRemarks("");
        setSaveNameOpen(true);
    }, []);

    const closeSaveName = useCallback(() => {
        setSaveNameOpen(false);
        setMapName("");
        setMapRemarks("");
    }, []);

    const confirmLoadStoredMap = useCallback(() => {
        if (previewSelection === "live" || !onLoadMap || !focusedStcm) return;
        const norm = (p: string) => p.replace(/\\/g, "/").trim();
        const companionBmp = norm(stcmRelToCompanionBmpRel(focusedStcm));
        const sel = previewSelection !== "live" ? norm(previewSelection) : "";
        const isEditedVariantImage = /\.(bmp|png)$/i.test(sel) && sel !== companionBmp;
        if (isEditedVariantImage) {
            void onLoadMap(focusedStcm, {
                variantBmpRelativePath: sel,
            });
        } else {
            void onLoadMap(focusedStcm);
        }
        closeMain();
    }, [previewSelection, focusedStcm, onLoadMap, closeMain]);

    const existingLower = useMemo(
        () =>
            existingStorageMapStemSet(
                files,
                editedBmps.map((e) => e.path)
            ),
        [files, editedBmps]
    );

    const normalizedMapSearch = mapSearchQuery.trim().toLowerCase();
    const filteredFiles = useMemo(() => {
        if (!normalizedMapSearch) {
            return files;
        }
        return files.filter((f) => {
            const label = storageMapRowLabel(f).toLowerCase();
            const path = f.replace(/\\/g, "/").toLowerCase();
            return label.includes(normalizedMapSearch) || path.includes(normalizedMapSearch);
        });
    }, [files, normalizedMapSearch]);

    const enlargePreview = useMemo(() => {
        if (previewSelection === "live") {
            if (!liveMapPreviewUrl) {
                return {
                    canEnlarge: false,
                    src: null as string | null,
                    alt: "",
                };
            }
            return {
                canEnlarge: true,
                src: liveMapPreviewUrl,
                alt: "Current map",
            };
        }
        if (filePreviewFailed) {
            return {
                canEnlarge: false,
                src: null as string | null,
                alt: "",
            };
        }
        return {
            canEnlarge: true,
            src: getStorageMapPreviewBmpUrl(baseUrl, previewSelection),
            alt: `Preview for ${storageMapRowLabel(previewSelection)}`,
        };
    }, [previewSelection, liveMapPreviewUrl, filePreviewFailed, baseUrl]);

    const trimmed = mapName.trim();
    const nameTaken = trimmed.length > 0 && existingLower.has(trimmed.toLowerCase());

    let formatError: string | null = null;
    if (trimmed.length > 0) {
        try {
            validateAndNormalizeMapBaseName(trimmed);
        } catch (e) {
            formatError = e instanceof Error ? e.message : "Invalid name";
        }
    }

    const canSave =
        Boolean(trimmed) &&
        !nameTaken &&
        !formatError &&
        !listLoading &&
        !listError &&
        Boolean(onSave);

    const confirmSave = useCallback(async () => {
        if (!canSave || !onSave || saveSubmitBusy) return;
        setSaveSubmitBusy(true);
        try {
            await onSave(trimmed, mapRemarks.trim());
            closeSaveName();
            closeMain();
        } catch {
            /* error toast from MapManager */
        } finally {
            setSaveSubmitBusy(false);
        }
    }, [canSave, trimmed, mapRemarks, onSave, closeSaveName, closeMain, saveSubmitBusy]);

    const currentMapListButtonClass = (active: boolean) =>
        `w-full rounded-r5 border px-3 py-2 text-left text-h5 transition-colors ${
            active ? "border-primary2 bg-black80" : "border-black80 bg-black70 hover:bg-black80"
        }`;

    const storageRowClass = (active: boolean) =>
        `w-full rounded-r5 border px-3 py-2 text-left text-h5 transition-colors ${
            active ? "border-primary2 bg-black80" : "border-black80 bg-black70 hover:bg-black80"
        }`;

    /** Horizontal variant chips (not full-width list rows). */
    const variantChipRowClass = (active: boolean) =>
        `shrink-0 min-w-[8.5rem] max-w-[11rem] rounded-r5 border px-3 py-2 text-left text-h5 transition-colors ${
            active ? "border-primary2 bg-black80" : "border-black80 bg-black70 hover:bg-black80"
        }`;

    return (
        <>
            <FunctionalButton
                className="h-10 shrink-0 rounded-r5 border border-amber-700 bg-amber-500 px-3 text-white hover:bg-amber-400"
                title="Map Manager"
                onClick={() => setMainOpen(true)}
            />
            <AnimatedModal
                visible={mainOpen}
                onClose={closeMain}
                className="flex h-[min(90vh,44rem)] min-h-[min(90vh,44rem)] w-full max-w-3xl flex-col overflow-hidden rounded-r10 border border-black80 bg-black90 p-4 shadow-lg"
                zIndexClassName="z-[10000]"
            >
                <div className="flex h-full min-h-0 flex-col">
                    <div className="mb-3 flex shrink-0 items-center justify-between gap-2">
                        <h3 className="text-h4">Map Manager</h3>
                        <CloseButton
                            className="text-black60 hover:text-black40"
                            onClick={closeMain}
                        />
                    </div>
                    <div className="flex min-h-0 flex-1 flex-row gap-4 overflow-hidden">
                        <div className="flex h-full min-h-0 w-1/2 min-w-0 shrink-0 flex-col">
                            <div className="mb-2 shrink-0 rounded-r5 border border-black80 bg-black70/30 p-2">
                                <p className="text-h5 mb-2 font-semibold text-black40">
                                    Current map
                                </p>
                                <button
                                    type="button"
                                    className={currentMapListButtonClass(
                                        previewSelection === "live"
                                    )}
                                    onClick={() => {
                                        setFocusedStcm(null);
                                        setPreviewSelection("live");
                                    }}
                                >
                                    Current map
                                </button>
                            </div>
                            <div className="mb-2 flex min-h-[10rem] flex-1 flex-col rounded-r5 border border-black80 bg-black70/30 p-2">
                                <p className="text-h5 mb-2 shrink-0 font-semibold text-black40">
                                    Storage — existing maps
                                </p>
                                <SearchBar
                                    className="mb-2 shrink-0"
                                    value={mapSearchQuery}
                                    onChange={() => {
                                        const input = document.activeElement;
                                        setMapSearchQuery(
                                            input instanceof HTMLInputElement ? input.value : ""
                                        );
                                    }}
                                />
                                <ul className="min-h-0 flex-1 space-y-1 overflow-y-auto pr-1">
                                    {listLoading ? (
                                        <li className="text-h5 py-2 text-black60">Loading list…</li>
                                    ) : files.length === 0 ? (
                                        <li className="text-h5 py-2 text-black60">
                                            No saved maps in storage.
                                        </li>
                                    ) : filteredFiles.length === 0 ? (
                                        <li className="text-h5 py-2 text-black60">
                                            No maps match this search.
                                        </li>
                                    ) : (
                                        <>
                                            {filteredFiles.map((f) => (
                                                <li key={f}>
                                                    <button
                                                        type="button"
                                                        className={storageRowClass(
                                                            focusedStcm === f
                                                        )}
                                                        onClick={() => {
                                                            setFocusedStcm(f);
                                                            setPreviewSelection(f);
                                                        }}
                                                    >
                                                        <span className="flex w-full items-start justify-between gap-2 text-left">
                                                            <span className="min-w-0 flex-1 break-words">
                                                                {storageMapRowLabel(f)}
                                                            </span>
                                                            {storageDatesLoading ? (
                                                                <span className="text-h5 shrink-0 text-black60">
                                                                    …
                                                                </span>
                                                            ) : storageSavedAtByFile[f] ? (
                                                                <span
                                                                    className="text-h5 shrink-0 text-black60"
                                                                    title={storageSavedAtByFile[f]}
                                                                >
                                                                    {formatSavedAtLabel(
                                                                        storageSavedAtByFile[f]
                                                                    )}
                                                                </span>
                                                            ) : null}
                                                        </span>
                                                    </button>
                                                </li>
                                            ))}
                                        </>
                                    )}
                                </ul>
                            </div>
                            {listError ? (
                                <p className="text-h5 mb-2 shrink-0 text-black40">
                                    Could not load map list.
                                </p>
                            ) : null}
                        </div>
                        <div className="flex h-full min-h-0 min-w-0 flex-1 flex-col border-l border-black80 pl-4">
                            <div className="mb-2 flex shrink-0 items-center justify-between gap-2">
                                <p className="text-h5 font-semibold text-black40">Preview</p>
                                <button
                                    type="button"
                                    title={
                                        enlargePreview.canEnlarge
                                            ? "Enlarge preview"
                                            : "No preview image to enlarge"
                                    }
                                    disabled={!enlargePreview.canEnlarge}
                                    onClick={() => setPreviewEnlargeOpen(true)}
                                    className="flex h-8 w-8 shrink-0 items-center justify-center rounded-r5 border border-black80 bg-black70 text-black40 transition-colors hover:border-primary2 hover:bg-black80 hover:text-white disabled:cursor-not-allowed disabled:border-black80 disabled:bg-black70/60 disabled:text-black60 disabled:hover:border-black80 disabled:hover:bg-black70/60 disabled:hover:text-black60"
                                >
                                    <EnlargePreviewIcon className="pointer-events-none !h-4 !w-4 shrink-0" />
                                </button>
                            </div>
                            {previewSelection === "live" ? (
                                liveMapPreviewUrl ? (
                                    <div className="flex min-h-0 flex-1 items-center justify-center overflow-hidden rounded-r5 border border-black80 bg-black70 p-2">
                                        <img
                                            src={liveMapPreviewUrl}
                                            alt="Current map"
                                            className="max-h-full w-full max-w-full object-contain"
                                        />
                                    </div>
                                ) : (
                                    <div className="flex min-h-[14rem] flex-1 items-center justify-center rounded-r5 border border-dashed border-black80 bg-black70/50 p-4">
                                        <p className="text-h5 text-center text-black60">
                                            No live map to preview
                                        </p>
                                    </div>
                                )
                            ) : (
                                <div className="flex min-h-0 flex-1 flex-col gap-2 overflow-hidden">
                                    {filePreviewFailed ? (
                                        <div className="flex min-h-[10rem] flex-1 flex-col items-center justify-center overflow-hidden rounded-r5 border border-black80 bg-black70 p-4">
                                            <p className="text-h5 text-center text-black60">
                                                No preview image for this map.
                                            </p>
                                        </div>
                                    ) : (
                                        <div className="flex min-h-0 min-h-[10rem] flex-1 items-center justify-center overflow-hidden rounded-r5 border border-black80 bg-black70 p-2">
                                            <img
                                                key={previewSelection}
                                                src={getStorageMapPreviewBmpUrl(
                                                    baseUrl,
                                                    previewSelection
                                                )}
                                                alt={`Preview for ${storageMapRowLabel(
                                                    previewSelection
                                                )}`}
                                                className="max-h-full w-full max-w-full object-contain"
                                                onError={() => setFilePreviewFailed(true)}
                                            />
                                        </div>
                                    )}
                                    <div className="flex min-h-[13rem] shrink-0 flex-col gap-1">
                                        {focusedStcm ? (
                                            <>
                                                <div className="flex items-center justify-between gap-2">
                                                    <p className="text-h5 font-semibold text-black40">
                                                        Remark
                                                    </p>
                                                    {!storageMetaLoading &&
                                                    !storageRemarkModalOpen ? (
                                                        <button
                                                            type="button"
                                                            title="Edit remark"
                                                            disabled={
                                                                listLoading || Boolean(listError)
                                                            }
                                                            onClick={() => {
                                                                setStorageRemarkSaveError(null);
                                                                setStorageRemarkDraft(
                                                                    displayedStorageRemark
                                                                );
                                                                setStorageRemarkModalOpen(true);
                                                            }}
                                                            className="flex shrink-0 items-center justify-center rounded-r5 p-1.5 text-black60 transition-colors hover:bg-black80 hover:text-black40 disabled:pointer-events-none disabled:opacity-40"
                                                        >
                                                            <PencilIcon className="h-4 w-4 [&_path]:fill-current" />
                                                        </button>
                                                    ) : null}
                                                </div>
                                                {storageMetaLoading ? (
                                                    <p className="text-h5 text-black60">
                                                        Loading remarks…
                                                    </p>
                                                ) : (
                                                    <div className="max-h-24 overflow-y-auto rounded-r5 border border-black80 bg-black70/30 p-2">
                                                        {displayedStorageRemark ? (
                                                            <p className="text-h5 whitespace-pre-wrap text-black40">
                                                                {displayedStorageRemark}
                                                            </p>
                                                        ) : (
                                                            <p className="text-h5 italic text-black60">
                                                                No remarks
                                                            </p>
                                                        )}
                                                    </div>
                                                )}
                                                <div className="mt-2 flex min-h-[7rem] flex-col border-t border-black80 pt-2">
                                                    <p className="text-h5 mb-1.5 shrink-0 font-semibold text-black40">
                                                        Edited map variants
                                                    </p>
                                                    {storageMetaLoading ? (
                                                        <p className="text-h5 flex flex-1 items-center py-1 text-black60">
                                                            Loading variants…
                                                        </p>
                                                    ) : storageMeta?.edited_bmp_variants &&
                                                      storageMeta.edited_bmp_variants.length > 0 ? (
                                                        <ul className="flex min-h-[5rem] flex-nowrap items-center gap-2 overflow-x-auto overflow-y-hidden pb-1 pr-1">
                                                            {storageMeta.edited_bmp_variants.map(
                                                                (v) => (
                                                                    <li
                                                                        key={v.bmp_rel_path}
                                                                        className="shrink-0"
                                                                    >
                                                                        <button
                                                                            type="button"
                                                                            className={variantChipRowClass(
                                                                                previewSelection ===
                                                                                    v.bmp_rel_path
                                                                            )}
                                                                            onClick={() =>
                                                                                setPreviewSelection(
                                                                                    v.bmp_rel_path
                                                                                )
                                                                            }
                                                                            title={
                                                                                v.saved_at ??
                                                                                undefined
                                                                            }
                                                                        >
                                                                            <span className="flex flex-col items-stretch gap-0.5 text-left">
                                                                                <span className="line-clamp-2 break-words">
                                                                                    {storageMapRowLabel(
                                                                                        v.bmp_rel_path
                                                                                    )}
                                                                                </span>
                                                                                {v.saved_at ? (
                                                                                    <span className="text-h5 shrink-0 text-black60">
                                                                                        {formatSavedAtLabel(
                                                                                            v.saved_at
                                                                                        )}
                                                                                    </span>
                                                                                ) : null}
                                                                            </span>
                                                                        </button>
                                                                    </li>
                                                                )
                                                            )}
                                                        </ul>
                                                    ) : (
                                                        <p className="text-h5 flex flex-1 items-center py-1 text-black60">
                                                            No edited variants for this map.
                                                        </p>
                                                    )}
                                                </div>
                                            </>
                                        ) : null}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                    <div className="mt-auto flex shrink-0 flex-wrap items-center justify-between gap-2 border-t border-black80 pt-3">
                        {previewSelection !== "live" ? (
                            <>
                                <div className="flex min-w-0 flex-wrap items-center gap-2">
                                    <FunctionalButton
                                        className="text-h5 rounded-r5 border border-red-900/80 bg-black70 px-4 py-2 hover:bg-black80"
                                        titleClassName="!normal-case text-red-400"
                                        title="Delete"
                                        disabled={listLoading}
                                        onClick={openDeleteConfirm}
                                    />
                                </div>
                                <div className="flex flex-wrap justify-end gap-2">
                                    {onEditStorageMap ? (
                                        <FunctionalButton
                                            className="rounded-r5 border border-black80 bg-black70 px-4 py-2 hover:bg-black80"
                                            title="Edit"
                                            disabled={listLoading || !focusedStcm}
                                            onClick={() => {
                                                const rel =
                                                    previewSelection !== "live" &&
                                                    /\.(bmp|png)$/i.test(previewSelection)
                                                        ? previewSelection
                                                        : focusedStcm;
                                                if (!rel) return;
                                                if (!focusedStcm) return;
                                                onEditStorageMap(rel, {
                                                    focusedStcm,
                                                    previewSelection,
                                                });
                                                closeMain();
                                            }}
                                        />
                                    ) : null}
                                    {onLoadMap && focusedStcm ? (
                                        <FunctionalButton
                                            className="hover:bg-primary1 rounded-r5 bg-primary2 px-4 py-2"
                                            title="Load map"
                                            onClick={confirmLoadStoredMap}
                                        />
                                    ) : null}
                                </div>
                            </>
                        ) : onStcmClearMap || onSave ? (
                            <>
                                <div className="flex min-w-0 flex-wrap items-center gap-2">
                                    {onStcmClearMap ? (
                                        <FunctionalButton
                                            className="text-h5 rounded-r5 border border-red-900/80 bg-black70 px-4 py-2 hover:bg-black80"
                                            titleClassName="!normal-case text-red-400"
                                            title="Clear map"
                                            onClick={() => setClearMapModalOpen(true)}
                                        />
                                    ) : null}
                                </div>
                                <div className="flex flex-wrap justify-end gap-2">
                                    {onSave ? (
                                        <FunctionalButton
                                            className="hover:bg-primary1 rounded-r5 bg-primary2 px-4 py-2"
                                            title="Save map"
                                            onClick={openSaveName}
                                        />
                                    ) : null}
                                </div>
                            </>
                        ) : (
                            <div className="h-10" aria-hidden />
                        )}
                    </div>
                </div>
            </AnimatedModal>
            <AnimatedModal
                visible={storageRemarkModalOpen}
                onClose={closeStorageRemarkModal}
                className="w-full max-w-md rounded-r10 border border-black80 bg-black90 p-4 shadow-lg"
                zIndexClassName="z-[10004]"
            >
                <div className="mb-3 flex items-center justify-between gap-2">
                    <h3 className="text-h4">Edit remark</h3>
                    <CloseButton
                        className="text-black60 hover:text-black40"
                        onClick={closeStorageRemarkModal}
                    />
                </div>
                {focusedStcm ? (
                    <p className="text-h5 mb-3 text-black40">
                        Map:{" "}
                        <span className="text-h5 font-semibold text-black60">
                            {storageMapRowLabel(focusedStcm)}
                        </span>
                    </p>
                ) : null}
                <label className="text-h5 mb-1 block text-black40">Remark</label>
                <textarea
                    value={storageRemarkDraft}
                    onChange={(e) =>
                        setStorageRemarkDraft(e.target.value.slice(0, STCM_REMARKS_MAX_LENGTH))
                    }
                    rows={5}
                    autoFocus
                    className="text-h5 mb-2 w-full resize-y rounded-r5 border border-black80 bg-black70 px-3 py-2 outline-none focus:border-primary2"
                    placeholder="Optional notes for this map"
                />
                <p className="text-h5 mb-2 text-right text-black60">
                    {storageRemarkDraft.length}/{STCM_REMARKS_MAX_LENGTH}
                </p>
                {storageRemarkSaveError ? (
                    <p className="text-h5 mb-2 text-black40">{storageRemarkSaveError}</p>
                ) : null}
                <div className="mt-4 flex flex-wrap items-center justify-between gap-2 border-t border-black80 pt-3">
                    <FunctionalButton
                        className="rounded-r5 border border-black80 bg-black70 px-4 py-2 hover:bg-black80"
                        title="Cancel"
                        disabled={storageRemarkSaving}
                        onClick={closeStorageRemarkModal}
                    />
                    <FunctionalButton
                        className="hover:bg-primary1 rounded-r5 bg-primary2 px-4 py-2"
                        title="Confirm"
                        disabled={storageRemarkSaving}
                        onClick={confirmStorageRemark}
                    />
                </div>
            </AnimatedModal>
            <AnimatedModal
                visible={previewEnlargeOpen}
                onClose={closePreviewEnlarge}
                className="flex max-h-[92vh] w-full max-w-6xl flex-col overflow-hidden rounded-r10 border border-black80 bg-black90 shadow-lg"
                zIndexClassName="z-[10005]"
            >
                <div className="min-h-0 flex-1 overflow-auto p-4">
                    {enlargePreview.canEnlarge && enlargePreview.src ? (
                        <img
                            src={enlargePreview.src}
                            alt={enlargePreview.alt}
                            className="mx-auto max-h-[min(78vh,52rem)] w-full object-contain"
                        />
                    ) : (
                        <p className="text-h5 py-12 text-center text-black60">No preview to show</p>
                    )}
                </div>
                <div className="flex shrink-0 justify-center border-t border-black80 p-4">
                    <FunctionalButton
                        className="rounded-r5 border border-black80 bg-black70 px-6 py-2 hover:bg-black80"
                        title="Close"
                        onClick={closePreviewEnlarge}
                    />
                </div>
            </AnimatedModal>
            <StcmClearMapDialog
                visible={clearMapModalOpen}
                onClose={() => setClearMapModalOpen(false)}
                onClear={onStcmClearMap}
                onChooseSave={onClearMapChooseSave}
                zClass="z-[10003]"
            />
            <AnimatedModal
                visible={deleteConfirmOpen}
                onClose={closeDeleteConfirm}
                className="w-full max-w-md rounded-r10 border border-black80 bg-black90 p-4 shadow-lg"
                zIndexClassName="z-[10002]"
            >
                <div className="mb-2 flex items-center justify-between gap-2">
                    <h3 className="text-h4">Delete stored map</h3>
                    <CloseButton
                        className="text-black60 hover:text-black40"
                        onClick={closeDeleteConfirm}
                    />
                </div>
                <p className="text-h5 mb-3 text-black40">
                    Deleting{" "}
                    <span className="text-h5 font-semibold text-black60">
                        {deleteTargetPath ? storageMapRowLabel(deleteTargetPath) : "this map"}
                    </span>{" "}
                    cannot be undone.
                </p>
                <p className="text-h5 mb-2 text-black40">
                    Type{" "}
                    <span className="font-mono text-h5 font-black tracking-wider text-black40">
                        CONFIRM
                    </span>{" "}
                    for deletion
                </p>
                <input
                    type="text"
                    autoComplete="off"
                    value={deleteConfirmInput}
                    onChange={(e) => setDeleteConfirmInput(e.target.value.toUpperCase())}
                    className="font-mono text-h5 mb-2 w-full rounded-r5 border border-black80 bg-black70 px-3 py-2 outline-none focus:border-primary2"
                    placeholder="CONFIRM"
                />
                {deleteError ? (
                    <p className="text-h5 mb-2 text-black40" title={deleteError}>
                        Could not delete: {deleteError}
                    </p>
                ) : null}
                <div className="mt-4 flex flex-wrap items-center justify-between gap-2 border-t border-black80 pt-3">
                    <FunctionalButton
                        className="rounded-r5 border border-black80 bg-black70 px-4 py-2 hover:bg-black80"
                        title="Cancel"
                        onClick={closeDeleteConfirm}
                    />
                    <div className="flex flex-wrap justify-end gap-2">
                        {deleteConfirmInput === "CONFIRM" ? (
                            <FunctionalButton
                                className="text-h5 rounded-r5 border border-red-900/80 bg-black70 px-4 py-2 hover:bg-black80"
                                titleClassName="!normal-case text-red-400"
                                title="Delete"
                                disabled={deleteBusy || !deleteTargetPath}
                                onClick={executeDeleteStoredMap}
                            />
                        ) : null}
                    </div>
                </div>
            </AnimatedModal>
            <AnimatedModal
                visible={saveNameOpen}
                onClose={closeSaveName}
                className="w-full max-w-md rounded-r10 border border-black80 bg-black90 p-4 shadow-lg"
                zIndexClassName="z-[10001]"
            >
                <div className="mb-3 flex items-center justify-between gap-2">
                    <h3 className="text-h4">Save map</h3>
                    <CloseButton
                        className="text-black60 hover:text-black40"
                        onClick={closeSaveName}
                    />
                </div>
                <label className="text-h5 mb-1 block text-black40">Map name</label>
                <input
                    type="text"
                    autoComplete="off"
                    value={mapName}
                    onChange={(e) => setMapName(e.target.value)}
                    className="text-h5 mb-3 w-full rounded-r5 border border-black80 bg-black70 px-3 py-2 outline-none focus:border-primary2"
                    placeholder="e.g. floor1_north"
                />
                <label className="text-h5 mb-1 block text-black40">Remarks</label>
                <textarea
                    value={mapRemarks}
                    onChange={(e) =>
                        setMapRemarks(e.target.value.slice(0, STCM_REMARKS_MAX_LENGTH))
                    }
                    rows={3}
                    className="text-h5 mb-2 w-full resize-y rounded-r5 border border-black80 bg-black70 px-3 py-2 outline-none focus:border-primary2"
                    placeholder="Optional notes for this map"
                />
                <p className="text-h5 mb-2 text-right text-black60">
                    {mapRemarks.length}/{STCM_REMARKS_MAX_LENGTH}
                </p>
                {nameTaken ? (
                    <p className="text-h5 mb-2 text-black40">
                        A map with this name already exists.
                    </p>
                ) : null}
                {formatError ? <p className="text-h5 mb-2 text-black40">{formatError}</p> : null}
                {listError ? (
                    <p className="text-h5 mb-2 text-black40">
                        Could not load map list — unique name not verified. Try again.
                    </p>
                ) : null}
                <div className="mt-4 flex flex-wrap justify-end gap-2 border-t border-black80 pt-3">
                    <FunctionalButton
                        className="rounded-r5 border border-black80 bg-black70 px-4 py-2 hover:bg-black80"
                        title="Cancel"
                        disabled={saveSubmitBusy}
                        onClick={closeSaveName}
                    />
                    <FunctionalButton
                        className="hover:bg-primary1 rounded-r5 bg-primary2 px-4 py-2"
                        title={saveSubmitBusy ? "Saving…" : "Save map"}
                        disabled={!canSave || saveSubmitBusy}
                        onClick={confirmSave}
                    />
                </div>
            </AnimatedModal>
        </>
    );
};

const MapView = (props: MapViewProps) => {
    const {
        mapId,
        mapMetadata,
        mapViewData,
        selectedPoint = null,
        robots = [],
        landmarks = [],
        waypoints = EMPTY_WAYPOINTS,
        plannedPath = EMPTY_PLANNED_PATH,
        onClick = () => {},
        robotOnClick,
        landmarkOnClick = () => {},
        waypointOnClick,
        missionActionMarkers = EMPTY_ACTION_MARKERS,
        onCreateClick,
        onChooseClick,
        menuVisible = false,
        menuPosition,
        menuOptions = [],
        onMenuClose = () => {},
        stcmListBaseUrl: stcmListBaseUrlProp,
        onStcmLoadMap,
        onStcmSaveMap,
        onStcmClearMap,
        hideStcmToolbar = false,
        hideMappingToggle = false,
        mappingToggleLabel,
        onStcmEditStorageMap,
        fitMapToBoundsOnLoad = false,
        sketchPolylines = [],
        onSketchMapClick,
        sketchDisableMapDrag = false,
        sketchPolylineStyle,
        onSketchPolylineClick,
        onNavigateToPoseDrag,
        mapLoading = false,
        missionPreviewFilterYaml,
        missionPreviewOverlayGridSource,
    } = props;

    const stcmListBaseUrl =
        stcmListBaseUrlProp === null ? null : stcmListBaseUrlProp ?? getStcmMapListBaseUrl();

    const showMap = useMemo(
        () => !!mapId && !!mapMetadata && !!mapViewData,
        [mapId, mapMetadata, mapViewData]
    );

    const canDrag = useMemo(() => !!mapId && !sketchDisableMapDrag, [mapId, sketchDisableMapDrag]);

    const mapCenter = useMemo(() => {
        if (!mapMetadata) return { lat: 0, lng: 0 };
        const widthMeters = mapMetadata.width * mapMetadata.resolution;
        const heightMeters = mapMetadata.height * mapMetadata.resolution;
        return {
            lng: mapMetadata.origin.x + widthMeters / 2,
            lat: mapMetadata.origin.y + heightMeters / 2,
        };
    }, [mapMetadata]);

    const [saveModalRequestSerial] = useState(0);
    const [mapManagerSaveNameFlowSerial, setMapManagerSaveNameFlowSerial] = useState(0);
    const [followRobot, setFollowRobot] = useState(true);
    const [navigatePoseMode, setNavigatePoseMode] = useState(false);
    const [dragStartPoint, setDragStartPoint] = useState<Point2D | null>(null);
    const [dragCurrentPoint, setDragCurrentPoint] = useState<Point2D | null>(null);

    const dragStartPointRef = useRef<Point2D | null>(null);
    const dragCurrentPointRef = useRef<Point2D | null>(null);
    const suppressNextMapClickRef = useRef(false);
    const dragHeadingTheta = useMemo(() => {
        if (!dragStartPoint || !dragCurrentPoint) return 0;
        const theta = Math.atan2(
            dragCurrentPoint.y - dragStartPoint.y,
            dragCurrentPoint.x - dragStartPoint.x
        );
        return theta;
    }, [dragStartPoint, dragCurrentPoint]);
    const dragHeadingPose = useMemo(() => {
        if (!dragStartPoint || !dragCurrentPoint) return null;
        const halfTheta = dragHeadingTheta / 2;
        return {
            position: { x: dragStartPoint.x, y: dragStartPoint.y, z: 0 },
            orientation: {
                x: 0,
                y: 0,
                z: Math.sin(halfTheta),
                w: Math.cos(halfTheta),
            },
        };
    }, [dragStartPoint, dragCurrentPoint, dragHeadingTheta]);

    const handleNavPoseDragStart = useCallback((point: Point2D) => {
        dragStartPointRef.current = point;
        dragCurrentPointRef.current = point;
        setDragStartPoint(point);
        setDragCurrentPoint(point);
    }, []);

    const handleNavPoseDragMove = useCallback((point: Point2D) => {
        if (!dragStartPointRef.current) return;
        dragCurrentPointRef.current = point;
        setDragCurrentPoint(point);
    }, []);

    const handleNavPoseDragEnd = useCallback(
        (end: Point2D) => {
            const start = dragStartPointRef.current;
            if (!start || !onNavigateToPoseDrag) return;
            const dx = end.x - start.x;
            const dy = end.y - start.y;
            const dragLength = Math.hypot(dx, dy);
            const theta = dragLength > 0.05 ? Math.atan2(dy, dx) : 0;
            onNavigateToPoseDrag({
                x: start.x,
                y: start.y,
                theta,
            });
            dragStartPointRef.current = null;
            dragCurrentPointRef.current = null;
            suppressNextMapClickRef.current = true;
            setDragStartPoint(null);
            setDragCurrentPoint(null);
            setNavigatePoseMode(false);
        },
        [onNavigateToPoseDrag]
    );

    useEffect(() => {
        if (navigatePoseMode) return;
        dragStartPointRef.current = null;
        dragCurrentPointRef.current = null;
        setDragStartPoint(null);
        setDragCurrentPoint(null);
    }, [navigatePoseMode]);

    const passLatLngToOnClick = useCallback(
        ({ latlng }: L.LeafletMouseEvent) => {
            if (suppressNextMapClickRef.current) {
                suppressNextMapClickRef.current = false;
                return;
            }
            if (navigatePoseMode) return;
            const pt = latLngToPoint2D(latlng);
            onClick(pt);
            onSketchMapClick?.(pt);
        },
        [navigatePoseMode, onClick, onSketchMapClick]
    );

    const bounds = useMemo(() => {
        return mapMetadataToBounds(mapMetadata);
    }, [mapMetadata]);

    useEffect(() => {
        if (!mapId?.startsWith("mission-preview:") || !mapMetadata || !bounds || !mapViewData) {
            return;
        }
        const sw = bounds.getSouthWest();
        const ne = bounds.getNorthEast();
        const filterYaml = missionPreviewFilterYaml ?? {
            resolution: mapMetadata.resolution,
            originX: mapMetadata.origin.x,
            originY: mapMetadata.origin.y,
            originYaw: 0,
        };
        logMissionMapPreviewSizes("ImageOverlay layer", {
            mapId,
            previewImagePx: { width: mapMetadata.width, height: mapMetadata.height },
            filterYaml,
            overlayGridCells: { width: mapMetadata.width, height: mapMetadata.height },
            leafletBounds: {
                sw: { lat: sw.lat, lng: sw.lng },
                ne: { lat: ne.lat, lng: ne.lng },
            },
            overlayGridSource: missionPreviewOverlayGridSource,
        });
    }, [
        mapId,
        mapViewData,
        mapMetadata,
        bounds,
        missionPreviewFilterYaml,
        missionPreviewOverlayGridSource,
    ]);

    const waypointPositions = useMemo(() => {
        return waypoints.map((waypoint) => {
            const latLng = point2DToLatLng({
                x: waypoint.pose.position.x,
                y: waypoint.pose.position.y,
            });
            return [latLng.lat, latLng.lng] as [number, number];
        });
    }, [waypoints]);

    const plannedPathPositions = useMemo(() => {
        return plannedPath.map((point) => {
            const latLng = point2DToLatLng(point);
            return [latLng.lat, latLng.lng] as [number, number];
        });
    }, [plannedPath]);

    const showPlannedPath = plannedPathPositions.length >= 2;

    const plannedPathLineOptions = useMemo(
        () => ({
            ...plannedPathOptions,
            weight: hideStcmToolbar ? 4 : plannedPathOptions.weight,
        }),
        [hideStcmToolbar]
    );

    const sketchPolylineOpts = useMemo(() => {
        return { ...sketchPathOptions, ...sketchPolylineStyle };
    }, [sketchPolylineStyle]);

    const requestMapManagerSaveFlow = useCallback(
        () => setMapManagerSaveNameFlowSerial((n) => n + 1),
        []
    );

    const robotFollowTarget = useMemo(() => {
        if (!robots?.length || !robots[0]?.pose?.position) return null;
        const pos = robots[0].pose.position;
        return point2DToLatLng({ x: pos.x, y: pos.y });
    }, [robots]);

    const hasStcmToolbar = Boolean(
        !hideStcmToolbar && stcmListBaseUrl && (onStcmLoadMap || onStcmSaveMap)
    );
    const clearDialogOnChooseSave =
        hasStcmToolbar && onStcmSaveMap ? requestMapManagerSaveFlow : undefined;
    const stcmToolbar = hasStcmToolbar ? (
        <div className="pointer-events-auto absolute right-14 top-2 z-[1001] flex max-w-[calc(100%-4rem)] flex-wrap items-center justify-end gap-2">
            <StcmMapManagerControl
                baseUrl={stcmListBaseUrl!}
                onLoadMap={onStcmLoadMap}
                onEditStorageMap={onStcmEditStorageMap}
                onSave={onStcmSaveMap}
                onStcmClearMap={onStcmClearMap}
                onClearMapChooseSave={clearDialogOnChooseSave}
                saveModalRequestSerial={saveModalRequestSerial}
                mapManagerSaveNameFlowSerial={mapManagerSaveNameFlowSerial}
                liveMapPreviewUrl={mapViewData}
            />
            {onStcmClearMap ? (
                <StcmClearMapControl
                    onClear={onStcmClearMap}
                    onChooseSave={clearDialogOnChooseSave}
                />
            ) : null}
        </div>
    ) : null;

    if (!showMap) {
        return (
            <div className="relative h-full w-full">
                {stcmToolbar}
                <MapViewFallback
                    loading={mapLoading}
                    onCreateClick={onCreateClick}
                    onChooseClick={onChooseClick}
                />
            </div>
        );
    }

    return (
        <MapContainer
            {...(fitMapToBoundsOnLoad ? { key: mapId } : {})}
            {...MAP_OPTS}
            {...(fitMapToBoundsOnLoad
                ? {
                      bounds: bounds ?? undefined,
                      boundsOptions: {
                          padding: MAP_INITIAL_FIT_PADDING,
                          maxZoom: MAP_INITIAL_FIT_MAX_ZOOM,
                      },
                  }
                : {
                      center: mapCenter,
                      zoom: 5,
                  })}
            maxZoom={MAP_INITIAL_FIT_MAX_ZOOM}
            dragging={canDrag}
            scrollWheelZoom={!sketchDisableMapDrag}
            touchZoom={!sketchDisableMapDrag}
            className="position-relative h-full w-full"
            doubleClickZoom={false}
            boxZoom={false}
            // style is needed to override leaflet-container style because it's appended to the className above
            style={{ backgroundColor: "#c7c7c7" }}
        >
            {fitMapToBoundsOnLoad ? <MapInitialFitBounds bounds={bounds} /> : null}
            {sketchDisableMapDrag || navigatePoseMode ? <SketchMapInteractionLock /> : null}
            <MapContainerResizeSync />
            <MapZoomInvalidateSync />
            <PlannedPathPane />
            {navigatePoseMode && onNavigateToPoseDrag ? (
                <NavigateToPoseDragController
                    enabled={navigatePoseMode}
                    onDragStart={handleNavPoseDragStart}
                    onDragMove={handleNavPoseDragMove}
                    onDragEnd={handleNavPoseDragEnd}
                />
            ) : null}
            <div className="pointer-events-auto absolute right-14 top-2 z-[1001] flex max-w-[calc(100%-4rem)] flex-wrap items-center justify-end gap-2">
                {mappingToggleLabel ? (
                    <span className="inline-flex h-10 shrink-0 items-center justify-center rounded-r5 bg-black100 px-3 text-sm font-medium tracking-wide text-white">
                        {mappingToggleLabel}
                    </span>
                ) : !hideMappingToggle ? (
                    <MappingToggleButton />
                ) : null}
                {hasStcmToolbar ? (
                    <StcmMapManagerControl
                        baseUrl={stcmListBaseUrl!}
                        onLoadMap={onStcmLoadMap}
                        onEditStorageMap={onStcmEditStorageMap}
                        onSave={onStcmSaveMap}
                        onStcmClearMap={onStcmClearMap}
                        onClearMapChooseSave={clearDialogOnChooseSave}
                        saveModalRequestSerial={saveModalRequestSerial}
                        mapManagerSaveNameFlowSerial={mapManagerSaveNameFlowSerial}
                        liveMapPreviewUrl={mapViewData}
                    />
                ) : null}
                {hasStcmToolbar && onStcmClearMap ? (
                    <StcmClearMapControl
                        onClear={onStcmClearMap}
                        onChooseSave={clearDialogOnChooseSave}
                    />
                ) : null}
            </div>
            {!sketchDisableMapDrag ? (
                <div className="pointer-events-auto absolute right-2 top-2 z-[1001] flex flex-col gap-2">
                    <FunctionalButton
                        className={`flex h-9 w-9 items-center justify-center rounded-full border shadow-md ${
                            followRobot
                                ? "border-primary2 bg-primary2 text-black"
                                : "border-black20 bg-white text-black80 hover:bg-black10"
                        }`}
                        icon={<CenterIcon className="h-5 w-5" />}
                        onClick={() => setFollowRobot((prev) => !prev)}
                    />
                    {onNavigateToPoseDrag ? (
                        <FunctionalButton
                            data-nav-pose-control="true"
                            className={`flex h-9 w-9 items-center justify-center rounded-full border shadow-md ${
                                navigatePoseMode
                                    ? "border-primary2 bg-primary2 text-black"
                                    : "border-black20 bg-white text-black80 hover:bg-black10"
                            }`}
                            icon={<NavArrowIcon className="h-5 w-5" />}
                            onClick={() => {
                                setNavigatePoseMode((prev) => !prev);
                                dragStartPointRef.current = null;
                                dragCurrentPointRef.current = null;
                                setDragStartPoint(null);
                                setDragCurrentPoint(null);
                            }}
                        />
                    ) : null}
                </div>
            ) : null}
            <RobotFollowController
                enabled={followRobot && !sketchDisableMapDrag && !navigatePoseMode}
                target={robotFollowTarget}
            />
            {bounds && mapViewData ? (
                <ImageOverlay
                    bounds={bounds}
                    url={mapViewData}
                    eventHandlers={{
                        click: passLatLngToOnClick,
                    }}
                    interactive={true}
                    bubblingMouseEvents={false}
                />
            ) : null}
            {dragStartPoint && dragCurrentPoint ? (
                <Polyline
                    key="navigate-pose-drag-preview"
                    interactive={false}
                    bubblingMouseEvents={false}
                    positions={[
                        [dragStartPoint.y, dragStartPoint.x],
                        [dragCurrentPoint.y, dragCurrentPoint.x],
                    ]}
                    pathOptions={{ color: "#22c55e", weight: 3, opacity: 0.95 }}
                />
            ) : null}
            {sketchPolylines.map((pts, idx) => {
                if (pts.length < 2) return null;
                const positions = pts.map((p) => [p.y, p.x] as [number, number]);
                if (onSketchPolylineClick) {
                    const hitWeight = Math.max(
                        typeof sketchPolylineOpts.weight === "number"
                            ? sketchPolylineOpts.weight
                            : 4,
                        18
                    );
                    return (
                        <Fragment key={`editor-sketch-${idx}`}>
                            <Polyline
                                interactive
                                bubblingMouseEvents={false}
                                positions={positions}
                                pathOptions={{
                                    ...sketchPolylineOpts,
                                    color: sketchPolylineOpts.color ?? "#000",
                                    weight: hitWeight,
                                    opacity: 0,
                                }}
                                eventHandlers={{
                                    click: (e) => {
                                        L.DomEvent.stopPropagation(e);
                                        onSketchPolylineClick(idx);
                                    },
                                }}
                            />
                            <Polyline
                                interactive={false}
                                bubblingMouseEvents={false}
                                positions={positions}
                                pathOptions={sketchPolylineOpts}
                            />
                        </Fragment>
                    );
                }
                return (
                    <Polyline
                        key={`editor-sketch-${idx}`}
                        interactive={false}
                        bubblingMouseEvents={false}
                        positions={positions}
                        pathOptions={sketchPolylineOpts}
                    />
                );
            })}
            <LandmarkMarkers landmarkOnClick={landmarkOnClick} landmarks={landmarks} />
            {/* {waypointPositions.length > 1 && ( */}
            <WaypointDirectionPath positions={waypointPositions} compact={hideStcmToolbar} />
            {/* )} */}
            {showPlannedPath ? (
                <Polyline
                    key="planned-path"
                    pane={PLANNED_PATH_PANE}
                    interactive={false}
                    positions={plannedPathPositions}
                    pathOptions={plannedPathLineOptions}
                />
            ) : null}
            <WaypointMarkers
                waypoints={waypoints}
                compact={hideStcmToolbar}
                waypointOnClick={waypointOnClick}
            />
            <MissionActionMarkers markers={missionActionMarkers} />
            <RobotMarkers robots={robots} robotOnClick={robotOnClick} />
            {selectedPoint && <SelectedPointMarker point={selectedPoint} />}
            {dragHeadingPose ? (
                <RobotMarker title="" pose={dragHeadingPose} interactive={false} />
            ) : dragStartPoint ? (
                <NavigateToPosePointMarker point={dragStartPoint} />
            ) : null}
            <PointActionMenu
                visible={menuVisible}
                position={menuPosition}
                options={menuOptions}
                onClose={onMenuClose}
            />
        </MapContainer>
    );
};

export default MapView;
